import { useMemo } from "react";

import EmptyState from "ds/components/EmptyState";
import { EmptystateVariablesColored } from "components/icons/generated";
import { Action, ConfigElement, ConfigType, IntentProject } from "types/generated";
import TableSimple from "ds/components/TableSimple";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import PageWrapper from "components/PageWrapper";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useIntentProjectConfig from "shared/IntentProject/useIntentProjectConfig";
import { checkAllowedAction } from "utils/access";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Counter from "ds/components/Counter";

import { showIntentProjectEnvironmentVariableFormDrawer } from "./VariableFormDrawer";
import { INTENT_PROJECT_ENVIRONMENT_VARIABLES_TABLE_CELLS } from "./VariablesTable/Cells";
import styles from "./styles.module.css";
import IntentProjectEnvironmentMountedFilesTable from "./MountedFilesTable";
import { showIntentProjectEnvironmentMountedFileFormDrawer } from "./MountedFileFormDrawer";
import useIntentProjectEnvironmentVariablesTableStore from "./VariablesTable/useIntentProjectEnvironmentVariablesTableStore";
import useIntentProjectEnvironmentMountedFilesTableStore from "./MountedFilesTable/useIntentProjectEnvironmentMountedFilesTableStore";

type IntentProjectEnvironmentProps = {
  intentProject: IntentProject;
};

const IntentProjectEnvironment = ({ intentProject }: IntentProjectEnvironmentProps) => {
  useTitle(`Environment · ${intentProject.name}`);

  const { configElements, loading, error } = useIntentProjectConfig(intentProject.id);

  const ErrorContent = useErrorHandle(error);

  const canAddConfig = checkAllowedAction(intentProject, Action.IntentProjectConfigAdd);

  const [environmentVariables, mountedFiles] = useMemo(
    () =>
      configElements.reduce<[ConfigElement[], ConfigElement[]]>(
        (acc, variable) => {
          if (variable.type === ConfigType.EnvironmentVariable) {
            acc[0].push(variable);
          } else {
            acc[1].push(variable);
          }
          return acc;
        },
        [[], []]
      ),
    [configElements]
  );

  const handleAddVariableDrawer = () => {
    showIntentProjectEnvironmentVariableFormDrawer({ intentProjectId: intentProject.id });
  };

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      <PageInfoHeader title="Environment" />

      <PageWrapper margin="0" padding="0" gap="x-large" direction="column" fullHeight>
        <CollapsibleSection
          title="Environment variables"
          ariaLevel={3}
          initialIsCollapsed={false}
          titleSuffix={<Counter count={environmentVariables.length} />}
          ariaLabel="Environment variables"
          fullbleed
          headerSuffix={
            <Tooltip
              on={(props) => (
                <Button
                  {...props}
                  variant="secondary"
                  onPress={handleAddVariableDrawer}
                  disabled={!canAddConfig.result}
                >
                  Add variable
                </Button>
              )}
              active={!!canAddConfig.message}
            >
              {canAddConfig.message}
            </Tooltip>
          }
        >
          {environmentVariables.length === 0 && (
            <EmptyState
              title="No variables yet"
              caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment."
              icon={EmptystateVariablesColored}
            />
          )}
          {environmentVariables.length > 0 && (
            <TableSimple
              data={environmentVariables}
              loading={loading}
              store={useIntentProjectEnvironmentVariablesTableStore}
              ariaLabel="Intent Project Environment table"
              cellPerColumn={INTENT_PROJECT_ENVIRONMENT_VARIABLES_TABLE_CELLS}
              hasPagination={false}
              wrapperClassName={styles.tableContainer}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          title="Mounted files"
          ariaLevel={3}
          titleSuffix={<Counter count={mountedFiles.length} />}
          ariaLabel="Mounted files"
          fullbleed
          headerSuffix={
            <Tooltip
              on={(props) => (
                <Button
                  {...props}
                  variant="secondary"
                  onPress={() =>
                    showIntentProjectEnvironmentMountedFileFormDrawer({
                      intentProjectId: intentProject.id,
                    })
                  }
                  disabled={!canAddConfig.result}
                >
                  Add file
                </Button>
              )}
              active={!!canAddConfig.message}
            >
              {canAddConfig.message}
            </Tooltip>
          }
        >
          {mountedFiles.length === 0 && (
            <EmptyState
              title="No files yet"
              caption="Mounted files are represented as a key-value mapping available to all processes running in a given environment."
              icon={EmptystateVariablesColored}
            />
          )}
          {mountedFiles.length > 0 && (
            <IntentProjectEnvironmentMountedFilesTable
              data={mountedFiles}
              store={useIntentProjectEnvironmentMountedFilesTableStore}
              ariaLabel="Intent Project Environment mounted files table"
              wrapperClassName={styles.tableContainer}
            />
          )}
        </CollapsibleSection>
      </PageWrapper>
    </>
  );
};

export default IntentProjectEnvironment;
